import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!token) {
            setError("This reset link is missing its token.");
            return;
        }
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/reset-password", { token, password });
            if (data.success) setDone(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-4 sm:px-6 py-20" data-testid="reset-password-page">
            <div className="panel p-8 sm:p-9">
                {done ? (
                    <div className="text-center py-6" data-testid="reset-success">
                        <span className="grid place-items-center w-16 h-16 mx-auto rounded-full bg-pos/10 text-pos">
                            <CheckCircle2 size={30} />
                        </span>
                        <h1 className="font-display text-2xl font-bold mt-5">Password updated</h1>
                        <p className="mt-3 text-sm text-dim">Your password has been reset successfully.</p>
                        <button
                            onClick={() => navigate("/login")}
                            data-testid="reset-goto-login"
                            className="mt-7 w-full rounded-full bg-accent text-accent-foreground py-3 text-sm font-semibold hover:brightness-95 hover:shadow-glow transition-[filter,box-shadow] duration-150"
                        >
                            Go to log in
                        </button>
                    </div>
                ) : (
                    <>
                        <span className="block h-0.5 w-10 rounded-full bg-accent mb-5" />
                        <span className="eyebrow text-accent">Account recovery</span>
                        <h1 className="font-display text-2xl sm:text-3xl font-bold mt-2.5">
                            Set a new password
                        </h1>
                        <p className="mt-2.5 text-sm text-dim leading-relaxed">Choose a new password for your account.</p>
                        <form onSubmit={submit} className="mt-8 space-y-5">
                            <div>
                                <label className="eyebrow text-dim block mb-2">New password</label>
                                <input
                                    type="password"
                                    required
                                    minLength={6}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="reset-password"
                                    className="field"
                                />
                            </div>
                            <div>
                                <label className="eyebrow text-dim block mb-2">Confirm password</label>
                                <input
                                    type="password"
                                    required
                                    minLength={6}
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    data-testid="reset-confirm"
                                    className="field"
                                />
                            </div>
                            {error && (
                                <p
                                    className="flex items-start gap-2.5 rounded-xl border border-neg/30 bg-neg/[0.07] p-3 text-sm text-neg"
                                    data-testid="reset-error"
                                >
                                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                                    {error}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="reset-submit"
                                className="w-full rounded-full bg-accent text-accent-foreground py-3 text-sm font-semibold hover:brightness-95 hover:shadow-glow transition-[filter,box-shadow] duration-150 disabled:opacity-50 disabled:hover:shadow-none"
                            >
                                {loading ? "Updating…" : "Reset password"}
                            </button>
                        </form>
                        <p className="mt-7 pt-6 border-t border-line text-sm text-dim">
                            <Link to="/login" className="text-accent link-underline font-medium">Back to log in</Link>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
